import * as THREE from 'three';
import { fetchModelBlob } from '@api/api';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

/**
 * Load a 3D model from a URL or File/Blob and return a THREE.BufferGeometry.
 * Supports .ply via PLYLoader and .obj via OBJLoader (only single-mesh OBJs).
 */
export async function loadThreeModel(source: string | File | Blob): Promise<THREE.BufferGeometry> {
  if (!source) throw new Error('Missing model source');

  let arrayBuffer: ArrayBuffer;
  let fileName: string;
  if (typeof source === 'string') {
    fileName = source;
    const blob = await fetchModelBlob(source);
    arrayBuffer = await blob.arrayBuffer();
  } else {
    fileName = (source as File).name || '';
    arrayBuffer = await source.arrayBuffer();
  }

  const ext = fileName.split('.').pop()?.toLowerCase();

  if (ext === 'ply') {
    const loader = new PLYLoader();
    const geometry = loader.parse(arrayBuffer);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    return geometry;
  } else if (ext === 'obj') {
    const text = new TextDecoder().decode(arrayBuffer);
    const loader = new OBJLoader();
    const obj = loader.parse(text);

    const meshes: THREE.Mesh[] = [];
    obj.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        meshes.push(child as THREE.Mesh);
      }
    });

    if (meshes.length === 0) throw new Error('No mesh found in OBJ file');
    if (meshes.length > 1) throw new Error('OBJ file contains more than one mesh');

    const geometry = meshes[0].geometry as THREE.BufferGeometry;
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    return geometry;
  } else {
    const loader = new PLYLoader();
    const geometry = loader.parse(arrayBuffer);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    return geometry;
  }
}
